type Edge<T> = [T, T];

export default function topologicalSort<T>(nodes: T[], edges: Edge<T>[]): T[] {
    let cursor = nodes.length;
    let i = cursor;
    const sorted = new Array(cursor);
    const visited: Record<number, boolean | undefined> = {};
    const outgoingEdges = makeOutgoingEdges(edges);
    const nodesHash = makeNodesHash(nodes);

    // check for unknown nodes
    edges.forEach(function (edge) {
        if (!nodesHash.has(edge[0]) || !nodesHash.has(edge[1])) {
            throw new Error(`Unknown node. There is an unknown node in the supplied edges: ${JSON.stringify(edge)}.`)
        }
    })

    while (i--) {
        if (!visited[i]) visit(nodes[i], i, new Set<T>())
    }

    return sorted;

    function visit(node: T, i: number, predecessors: Set<T>) {
        if (predecessors.has(node)) {
            let nodeRep;
            try {
                nodeRep = ", node was:" + JSON.stringify(node)
            } catch (e) {
                nodeRep = ""
            }
            throw new Error('Cyclic dependency' + nodeRep)
        }

        if (!nodesHash.has(node)) {
            throw new Error('Found unknown node. Make sure to provided all involved nodes. Unknown node: ' + JSON.stringify(node))
        }

        if (visited[i]) return;
        visited[i] = true

        const outgoing = Array.from(outgoingEdges.get(node) || new Set<T>());

        i = outgoing.length;
        if (i) {
            predecessors.add(node)
            do {
                const child = outgoing[--i];
                const nodeHash = nodesHash.get(child);
                if (nodeHash == undefined) {
                    throw new Error('Node hash is missing. Node: ' + JSON.stringify(node))
                }
                visit(child, nodeHash, predecessors)
            } while (i)
            predecessors.delete(node)
        }

        sorted[--cursor] = node
    }
}

function makeOutgoingEdges<T>(arr: Edge<T>[]) {
    const edges = new Map<T, Set<T>>();
    for (let i = 0, len = arr.length; i < len; i++) {
        const edge = arr[i];
        if (!edges.has(edge[0])) edges.set(edge[0], new Set<T>())
        if (!edges.has(edge[1])) edges.set(edge[1], new Set<T>())
        edges.get(edge[0])?.add(edge[1])
    }
    return edges
}

function makeNodesHash<T>(arr: T[]) {
    const res = new Map<T, number>();
    for (let i = 0, len = arr.length; i < len; i++) {
        res.set(arr[i], i)
    }
    return res
}
